import { ReactNode } from 'react';
import { Alert, Box, Spinner } from '@cloudscape-design/components';
import { Authenticator } from '@aws-amplify/ui-react';

import { useAuth } from '../hooks/use-auth';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Route guard component that protects child routes from unauthenticated access.
 *
 * - If no valid session exists, redirects to the Cognito hosted UI via Amplify Authenticator.
 * - If the token has expired, shows "Your session has expired. Please sign in again."
 * - While loading, shows a centered spinner.
 *
 * Requirements: 1.1, 1.3, 1.7
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isLoading, isAuthenticated, error } = useAuth();

  // Show loading state while checking session
  if (isLoading) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  // Session expired or error — show message and re-authenticate
  if (error || !isAuthenticated) {
    const isExpiry = error?.toLowerCase().includes('expired') ||
      error?.toLowerCase().includes('token');

    if (isExpiry) {
      return (
        <Box textAlign="center" padding={{ top: 'xxxl' }}>
          <Alert type="warning" header="Session expired">
            Your session has expired. Please sign in again.
          </Alert>
        </Box>
      );
    }

    // No valid session — use Amplify Authenticator to redirect to Cognito hosted UI
    return (
      <Authenticator variation="modal" loginMechanisms={['email']}>
        {() => <>{children}</>}
      </Authenticator>
    );
  }

  return <>{children}</>;
}
